import { userNeedsSubscription, userCanAccessFeature, defaultHomePathForUser } from './plan-access';

/** Allowed external return URLs (marketing site → app → marketing site). */
const ALLOWED_HOSTS = [
  'localhost',
  '127.0.0.1',
  'proteg.netlify.app',
  'gentle-buttercream-e0fe8b.netlify.app',
  'protego.ai',
  'www.protego.ai',
];

const REDIRECT_STORAGE_KEY = 'protego_auth_redirect';

const isDev = process.env.NODE_ENV === 'development';
const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API_ROOT = isDev ? '' : rawBackend;

export function isAllowedExternalRedirect(url) {
  if (!url || !/^https?:\/\//i.test(url)) return false;
  try {
    const { hostname, protocol } = new URL(url);
    if (protocol !== 'http:' && protocol !== 'https:') return false;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
    if (hostname.endsWith('.netlify.app')) return true;
    if (hostname === 'protego.ai' || hostname.endsWith('.protego.ai')) return true;
    return ALLOWED_HOSTS.includes(hostname);
  } catch {
    return false;
  }
}

export function rememberAuthRedirect(url) {
  if (isAllowedExternalRedirect(url)) {
    sessionStorage.setItem(REDIRECT_STORAGE_KEY, url);
  }
}

export function takeRememberedAuthRedirect() {
  const stored = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
  if (stored) {
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
  }
  return stored;
}

export function resolveAuthRedirect(redirect) {
  return redirect || takeRememberedAuthRedirect();
}

/** Strip legacy token fragments from marketing URLs without using them. */
export function stripLegacyAuthHash(url) {
  if (!url) return url;

  try {
    const parsed = new URL(url);
    const raw = parsed.hash.slice(1);
    if (!raw || (!raw.includes('protego_token=') && !raw.includes('protego_user='))) {
      return url;
    }

    const kept = raw
      .split('&')
      .filter((part) => part && !part.startsWith('protego_token=') && !part.startsWith('protego_user='));

    parsed.hash = kept.length ? `#${kept.join('&')}` : '';
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * Redirect back to the marketing site with a short-lived one-time handoff code.
 * The landing page exchanges the code server-side for the bearer token.
 */
export async function redirectToExternalAuth(url) {
  const cleanUrl = stripLegacyAuthHash(url);
  if (!cleanUrl) return;

  const token = localStorage.getItem('protego_token');
  if (!token) {
    window.location.replace(cleanUrl);
    return;
  }

  try {
    const handoffUrl = `${API_ROOT}/api/auth/handoff`;
    const res = await fetch(handoffUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
      body: JSON.stringify({ return_url: cleanUrl }),
    });

    if (!res.ok) {
      throw new Error('handoff failed');
    }

    const data = await res.json();
    if (!data?.code) {
      throw new Error('missing handoff code');
    }

    const parsed = new URL(cleanUrl);
    parsed.searchParams.set('auth_handoff', data.code);
    window.location.replace(parsed.toString());
  } catch {
    // Fall back to a clean redirect; landing can still sync via auth-bridge iframe.
    window.location.replace(cleanUrl);
  }
}

export async function followAuthRedirect(redirect, navigate, user) {
  if (user && userNeedsSubscription(user)) {
    navigate('/settings#plans', { replace: true });
    return;
  }

  const target = resolveAuthRedirect(redirect);

  if (isAllowedExternalRedirect(target)) {
    await redirectToExternalAuth(target);
    return;
  }

  if (user && !userCanAccessFeature(user, 'monitor')) {
    navigate(defaultHomePathForUser(user), { replace: true });
    return;
  }

  if (target && target.startsWith('/')) {
    navigate(target, { replace: true });
    return;
  }
  navigate('/', { replace: true });
}
